import { useState, useEffect, type MouseEvent } from 'react';
import jsCookie from 'js-cookie';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { Fire } from '@phosphor-icons/react';
import { Button, IconButton, Stack, Tooltip, Typography, Link } from '@unity/cloud-ui';
import { keyframes, styled } from '@unity/cloud-ui/styles';
import { getClientEnv } from '@/lib/constants/environment';

const { PREVIEW } = getClientEnv();

const GITHUB_BASE_URL = 'https://github.com/Unity-Technologies/docs-content/pull';

const previewIconKeyframe = keyframes`
    0% { color: #DC143C; }
    25% { color: #CE4320; }
    50% { color: #FFD700; }
    75% { color: #CE4320; }
    100% { color: #DC143C; }
`;

const StyledPreviewIcon = styled(Fire)(() => ({
  animation: `${previewIconKeyframe} 3.5s infinite linear`,
}));

export const Preview = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [previewVersion, setPreviewVersion] = useState(null);

  useEffect(() => {
    const cookiePreviewVersion = jsCookie.get(PREVIEW);

    if (cookiePreviewVersion) {
      setPreviewVersion(cookiePreviewVersion);
    }
  }, []);

  const handleOnLiveClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const params = new URLSearchParams(searchParams.toString());

    params.delete(PREVIEW);
    jsCookie.remove(PREVIEW);

    window.location.assign(`${pathname}?${params.toString()}`);
  };

  if (!previewVersion) {
    return null;
  }

  const pullRequestLink = `${GITHUB_BASE_URL}/${previewVersion}`;

  return (
    <Tooltip
      title={
        <Stack alignItems="center">
          <Typography fontWeight="bold">{t('header:preview-version:current-version-label')}</Typography>
          <Link href={pullRequestLink} external>
            {previewVersion}
          </Link>

          <Button variant="contained" onClick={handleOnLiveClick} sx={{ marginTop: 1 }}>
            {t('header:preview-version:use-live-version')}
          </Button>
        </Stack>
      }
      slotProps={{
        tooltip: {
          sx: {
            padding: 2,
          },
        },
      }}
    >
      <IconButton aria-label={t('header:preview-version:aria-label')}>
        <StyledPreviewIcon />
      </IconButton>
    </Tooltip>
  );
};
